import { useMyTeamsFarms } from '@/hooks/useMyTeamsFarms';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Users, Sprout } from 'lucide-react';
import { formatLocationForDisplay } from '@/utils/locationUtils';
import { FeedSkeleton } from '../skeleton/FeedSkeleton';

export const MyTeamsFarmsList = () => {
  const { farms, loading, error } = useMyTeamsFarms(true);

  if (loading) {
    return <FeedSkeleton />;
  }

  if (error) {
    return (
      <Card className="mx-3 md:mx-0">
        <CardContent className="p-6 text-center">
          <p className="text-red-600">Error loading team farms: {error}</p>
        </CardContent>
      </Card>
    );
  }

  if (farms.length === 0) {
    return (
      <Card className="mx-3 md:mx-0">
        <CardContent className="p-8 text-center">
          <Users className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Team Farms Yet</h3>
          <p className="text-gray-600 text-sm max-w-md mx-auto">
            Join teams or invite farmers to your teams to see their farms here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {farms.map((farm, index) => (
        <Card key={`${farm.id}-${farm.team_id}-${index}`} className="mx-3 md:mx-0 hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              {/* Avatar */}
              <div className="w-14 h-14 rounded-full border-2 border-green-200 overflow-hidden flex-shrink-0">
                {farm.avatar_url || farm.farm_logo_url ? (
                  <img
                    src={farm.avatar_url || farm.farm_logo_url || ''}
                    alt={farm.farm_name || farm.username || 'Farm'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-green-100 flex items-center justify-center">
                    <Sprout className="w-6 h-6 text-green-600" />
                  </div>
                )}
              </div>

              {/* Farm Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-gray-900 truncate">
                    {farm.farm_name || farm.username || 'Farm'}
                  </h3>
                  <span className="text-green-600 text-sm">✓</span>
                </div>
                
                {farm.username && (
                  <p className="text-sm text-gray-600 mb-1">@{farm.username}</p>
                )}

                {/* Team Badge */}
                <div className="flex items-center gap-1.5 mb-2">
                  <Users className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-xs font-medium text-blue-600">{farm.team_name}</span>
                </div>

                {farm.location && (
                  <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                    <MapPin className="w-4 h-4 text-green-600" />
                    <span className="truncate">{formatLocationForDisplay(farm.location, 'Location available')}</span>
                  </div>
                )}

                {farm.usda_zone && (
                  <div className="inline-flex items-center gap-1.5 bg-green-600 text-white rounded-full px-3 py-1">
                    <img
                      src="/lovable-uploads/c22db1b9-e0c9-405c-a328-60c97c2e4f7c.png"
                      alt="Zone"
                      className="w-3.5 h-3.5 object-cover rounded"
                    />
                    <span className="text-xs font-semibold">Zone {farm.usda_zone.replace('Zone ', '')}</span>
                  </div>
                )}

                {farm.bio && (
                  <p className="text-sm text-gray-700 mt-2 line-clamp-2">{farm.bio}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
